import classNames from 'classnames';

import AttributeType from 'types/Attribute';
import ExpandArrow from '@components/atoms/ExpandArrow';
import HtmlPreview from '@components/HtmlPreview';
import Collapsible from '@components/Collapsible';
import { Mode, Display } from '@templates/product/Attributes';
import getPlainText from 'utils/getPlainText';
import { IntlShape, useIntl } from 'utils/intl';

import classes from './Attribute.module.scss';
import messages from './Attribute.messages';

type DisplayMap = Record<Display, (intl: IntlShape, values: string[]) => JSX.Element[] | string>;

enum MODE {
  small = 'small',
  medium = 'medium',
  large = 'large',
  nested = 'nested',
}

enum DISPLAY {
  inline = 'inline',
  block = 'block',
}

const nameClassesMap: Record<MODE, string> = {
  [MODE.small]: 'nameSmall',
  [MODE.medium]: 'nameMedium',
  [MODE.large]: 'nameLarge',
  [MODE.nested]: 'nameNested',
};

const valueClassesMap: Record<MODE, string> = {
  [MODE.small]: 'valueSmall',
  [MODE.medium]: 'valueMedium',
  [MODE.large]: 'valueLarge',
  [MODE.nested]: 'valueNested',
};

const displayMap: DisplayMap = {
  [DISPLAY.inline]: (intl, values) =>
    values.map((value) => getPlainText(value) || intl.formatMessage(messages.none)).join(', '),
  [DISPLAY.block]: (intl, values) =>
    values.map((value, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <HtmlPreview key={`${value}-${i}`} htmlText={value || intl.formatMessage(messages.none)} />
    )),
};

type Props = {
  mode: Mode;
  display: Display;
  value: string[];
  attributes: AttributeType[];
  name: string;
};

const Attribute = ({ name, value, attributes, mode, display }: Props): JSX.Element => {
  const intl = useIntl();

  return attributes.length ? (
    <Collapsible
      trigger={(isOpened, handleClick) => (
        <div className={classNames(classes.trigger, { [classes.large]: mode === 'large' })} onClick={handleClick}>
          <div className={classes.wrapper}>
            <div className={classes[nameClassesMap[mode]]}>{name}</div>
            <div className={classes[valueClassesMap[mode]]}>{displayMap[display](intl, value)}</div>
          </div>
          <ExpandArrow className={classes.arrow} isOpened={isOpened} />
        </div>
      )}
    >
      <div
        className={classNames(classes.nestedAtrributes, {
          [classes.nestedAttributesPadding]: mode !== 'large',
          [classes.nestedAttributesPaddingLarge]: mode === 'large',
        })}
      >
        {attributes.map(({ id, name: nestedName, value: nestedValue, attributes: nestedAttributes }) => (
          <div key={id}>
            <Attribute
              mode="nested"
              name={nestedName}
              value={nestedValue}
              attributes={nestedAttributes}
              display={display}
            />
          </div>
        ))}
      </div>
    </Collapsible>
  ) : (
    <div className={classNames(classes.wrapper, { [classes.large]: mode === 'large' })}>
      <div className={classes[nameClassesMap[mode]]}>{name}</div>
      <div className={classes[valueClassesMap[mode]]}>{displayMap[display](intl, value)}</div>
    </div>
  );
};

export default Attribute;
